import type { HTMLAttributes } from 'react'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import { cn } from '@gto/ui/cn'

const badgeVariants = tv({
  variants: {
    variant: {
      default: [
        // Text styles — the SIZE axis owns font-size, line-height and height.
        'font-medium tracking-[0.14px] uppercase',
        // Container styles
        'rounded-lg border inset-2 min-w-5 p-1 inline-flex items-center justify-center',
      ],
      filled: [
        // Same typography + shape as default; border transparent so layout matches outlined siblings.
        'font-medium tracking-[0.14px] uppercase',
        'rounded-lg border border-transparent inset-2 min-w-5 p-1 inline-flex items-center justify-center',
      ],
      dot: 'size-2 rounded-[9999px] block',
    },
    /**
     * Badge had ONE hardcoded size (8px label, 14px tall) and no way to ask for another.
     * At 8px a multi-word product mark beside 14px body text reads as noise rather than a
     * label, which is what `sm` is for.
     *
     * This axis OWNS font-size, line-height and height. The first version left those baked
     * into each `variant` and relied on declaration order to outrank them, which meant every
     * new variant had to remember to repeat the type, and every new size had to out-specify
     * two copies of it. One axis, one owner.
     *
     * `leading-100` is spelled out on BOTH steps, not inherited: tailwind-merge treats a
     * `text-*` class as a font-size utility that may carry its own line-height, so a step
     * that omits it inherits the ancestor's line-height and renders far taller than its own
     * `h-*` (the 20px pill came out at 30px) — measured, not theorised.
     */
    size: {
      /** 8px label, 14px tall — the DS default and what every existing Badge renders. */
      xs: 'text-[8px] leading-100 h-3.5',
      /**
       * 10px label (`text-xs` = the DS `fontSizes[10]` step) in a 20px pill — the same 10px
       * the pricing page already uses for this pill role (`XS_CHIP`) and the step the "NEW"
       * promo chips sit on.
       *
       * h-5 is the exact fit, not a guess: 20px - 8px (`p-1`) - 2px border = the 10px line.
       */
      sm: 'text-xs leading-100 h-5',
    },
    color: {
      primary: '',
      secondary: '',
      error: '',
      warning: '',
      info: '',
      success: '',
    },
    position: {
      static: '',
      'top-right': 'absolute top-2 right-2 translate-x-1/2 -translate-y-1/2',
      'top-left': 'absolute top-2 left-2 -translate-x-1/2 -translate-y-1/2',
      'bottom-right': 'absolute bottom-2 right-2 translate-x-1/2 translate-y-1/2',
      'bottom-left': 'absolute bottom-2 left-2 -translate-x-1/2 translate-y-1/2',
    },
  },
  compoundVariants: [
    {
      // `dot` renders no children and owns its own square geometry, so no size step may
      // reach it. `size-2` and a step's `h-*` are the same tailwind-merge conflict group
      // and the size axis is declared later, so without this a `dot` asking for any size
      // renders an 8px-wide, 20px-tall lozenge instead of a dot. Restated here because a
      // compound variant merges after both axes, whichever size is passed.
      variant: 'dot',
      className: 'size-2',
    },
    {
      variant: 'default',
      color: 'primary',
      className: 'border-primary-main text-primary-main',
    },
    {
      variant: 'default',
      color: 'secondary',
      className: 'border-text-secondary text-text-secondary',
    },
    {
      variant: 'default',
      color: 'error',
      className: 'border-error-main text-error-main',
    },
    {
      variant: 'default',
      color: 'warning',
      className: 'border-warning-main text-warning-main',
    },
    {
      variant: 'default',
      color: 'info',
      className: 'border-info-main text-info-main',
    },
    {
      variant: 'default',
      color: 'success',
      className: 'border-success-main text-success-main',
    },
    { variant: 'dot', color: 'primary', className: 'bg-primary-main' },
    { variant: 'dot', color: 'secondary', className: 'bg-text-secondary' },
    { variant: 'dot', color: 'error', className: 'bg-error-main' },
    { variant: 'dot', color: 'warning', className: 'bg-warning-main' },
    { variant: 'dot', color: 'info', className: 'bg-info-main' },
    { variant: 'dot', color: 'success', className: 'bg-success-main' },
    {
      variant: 'filled',
      color: 'primary',
      className: 'bg-primary-main text-primary-contrastText',
    },
    {
      variant: 'filled',
      color: 'secondary',
      className: 'bg-secondary-main text-text-secondary',
    },
    {
      variant: 'filled',
      color: 'error',
      className: 'bg-error-main text-error-contrastText',
    },
    {
      variant: 'filled',
      color: 'warning',
      className: 'bg-warning-main text-warning-contrastText',
    },
    {
      variant: 'filled',
      color: 'info',
      className: 'bg-info-main text-info-contrastText',
    },
    {
      variant: 'filled',
      color: 'success',
      className: 'bg-success-main text-success-contrastText',
    },
  ],
  defaultVariants: {
    variant: 'default',
    color: 'primary',
    position: 'static',
    // `xs` is what every Badge rendered before the variant existed, so adding it changed
    // nothing on the ~110 existing call sites.
    size: 'xs',
  },
})

type BadgeVariants = VariantProps<typeof badgeVariants>

type BadgeProps = HTMLAttributes<HTMLSpanElement> & BadgeVariants

const Badge = ({ children, className, color, position, size, variant, ...props }: BadgeProps) => {
  return (
    <span
      className={cn(badgeVariants({ color, position, size, variant }), className)}
      {...props}
    >
      {variant !== 'dot' && children}
    </span>
  )
}

export { Badge }
export type { BadgeProps }
